/**
 * Mobile scouting target card for the All Targets list — "v7" layout.
 *
 * Row 1: initials avatar + player name + score badge.
 * Row 2 (meta): position chip · club · age · contract.
 * Row 3 (status): Starting XI chip + reports chip + pipeline link.
 * Row 4: removable watchlist pills + `+N` overflow.
 * Row 5: "updated · scout" footer.
 *
 * Rendered purely through the atom layer + shared molecules (InitialsAvatar,
 * RatingBadge, StatusChip, RemovablePill, MetaRow, PositionBadge,
 * ReportsChip — the latter two promoted from this card in SE-5527, classes
 * verbatim) with **semantic tokens** —
 * no `@/components/ui` primitives, no inline hex, no theme-keyed waivers.
 * NativeWind resolves light/dark from the tokens, so there is no `theme` prop.
 * Text size/line-height comes only from the `Text` variant (never inline —
 * SE-5397). Elements whose data the query doesn't supply (Starting XI, pipeline,
 * footer) hide gracefully.
 * @module features/scouting/components/MobileTargetCard/MobileTargetCardView
 */

import React, { memo } from "react";

import { ChevronRight } from "lucide-react-native";

import { TEST_ID_SCOUTING } from "e2e/testId";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import InitialsAvatar from "@/components/molecules/InitialsAvatar";
import MetaRow from "@/components/molecules/MetaRow";
import PositionBadge from "@/components/molecules/PositionBadge";
import RatingBadge from "@/components/molecules/RatingBadge";
import RemovablePill from "@/components/molecules/RemovablePill";
import ReportsChip from "@/components/molecules/ReportsChip";
import StatusChip from "@/components/molecules/StatusChip";

/** Max named watchlist pills shown before collapsing the rest into `+N` */
const MAX_PILLS = 2;

/** Props for MobileTargetCardView */
interface MobileTargetCardViewProps {
  /** Player display name */
  readonly playerName: string;
  /** 1–2 letter initials for the avatar circle (derived in the container) */
  readonly initials: string;
  /** Club name */
  readonly clubName: string;
  /** Player age in whole years — null hides the segment */
  readonly age: number | null;
  /** Months remaining on contract — null hides the segment */
  readonly monthsOnContract: number | null;
  /** GPR score — null renders nothing */
  readonly gpr: number | null;
  /** Short label prefix for the badge (e.g. "CAR"), null for default GPR */
  readonly scoreBadgeLabel: string | null;
  /**
   * Overall average scout rating — when present it prefixes the reports chip
   * ("3.2 · N reports"); null shows the plain count (SE-4143).
   */
  readonly avgScoutRating: number | null;
  /** Number of scout reports — drives the reports chip (SE-4143) */
  readonly scoutReportCount: number | null;
  /** Squad-status label for the Starting XI chip — null hides it */
  readonly squadStatus?: string | null;
  /** Pipeline tier for the "Pipeline: {tier}" link — null hides it */
  readonly pipelineTier?: string | null;
  /** Pre-formatted "Updated 2d ago" footer label — null hides it */
  readonly updatedLabel?: string | null;
  /** Scout / owner byline for the footer — null hides it */
  readonly scoutName?: string | null;
  /** Primary position abbreviation (e.g. "ST", "CAM") */
  readonly position: string;
  /** Positions beyond the primary — renders a `+N` indicator when > 0 (SE-4141) */
  readonly positionExtraCount: number;
  /** Callback when the position badge is tapped to view all positions (SE-4141) */
  readonly onPositionsPress: () => void;
  /** All watchlists the player belongs to (id + name) — renders pills + `+N` */
  readonly watchlists: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  /** Callback when a list pill (or its × / the card) is tapped to manage lists */
  readonly onListPress: () => void;
  /** Callback when the card is tapped */
  readonly onPress: () => void;
}

/** Props for the pipeline link */
interface PipelineLinkProps {
  readonly tier: string;
}

/**
 * "Pipeline: {tier}" accent link with a chevron.
 * @param root0 - Link data
 * @param root0.tier - Pipeline tier
 */
const PipelineLink = ({ tier }: PipelineLinkProps) => {
  return (
    <Stack direction="horizontal" className="shrink-0 items-center">
      <Text variant="caption" className="text-accent-primary">
        {`Pipeline: ${tier}`}
      </Text>
      <Icon as={ChevronRight} size="xs" className="text-accent-primary" />
    </Stack>
  );
};

/** Props for the status row */
interface StatusRowProps {
  readonly squadStatus: string | null | undefined;
  readonly avgScoutRating: number | null;
  readonly scoutReportCount: number | null;
  readonly pipelineTier: string | null | undefined;
}

/**
 * Status row: Starting XI chip + reports chip (left), pipeline link (right).
 * Each hides when its data is absent; the whole row hides when empty.
 * @param root0 - Status row data
 * @param root0.squadStatus - Squad-status label, or null
 * @param root0.avgScoutRating - Overall avg scout rating, or null
 * @param root0.scoutReportCount - Report count
 * @param root0.pipelineTier - Pipeline tier, or null
 */
const StatusRow = ({
  squadStatus,
  avgScoutRating,
  scoutReportCount,
  pipelineTier,
}: StatusRowProps) => {
  const hasReports = avgScoutRating != null || (scoutReportCount ?? 0) > 0;
  if (!squadStatus && !hasReports && !pipelineTier) return null;

  return (
    <Stack direction="horizontal" className="items-center justify-between">
      <Stack
        direction="horizontal"
        className="flex-1 flex-wrap items-center gap-1"
      >
        {squadStatus ? <StatusChip label={squadStatus} /> : null}
        <ReportsChip
          avgScoutRating={avgScoutRating}
          scoutReportCount={scoutReportCount}
          testID={
            avgScoutRating != null
              ? TEST_ID_SCOUTING.MOBILE_TARGET_AVG_RATING
              : undefined
          }
        />
      </Stack>
      {pipelineTier ? <PipelineLink tier={pipelineTier} /> : null}
    </Stack>
  );
};

/** Props for the watchlist pills row */
interface WatchlistPillsProps {
  readonly watchlists: readonly {
    readonly id: string;
    readonly name: string;
  }[];
  readonly onListPress: () => void;
}

/**
 * Removable watchlist pills + `+N` overflow. The × and the pill both open the
 * manage-watchlists sheet (instant per-pill removal is a future follow-up).
 * @param root0 - Watchlist data + handler
 * @param root0.watchlists - All watchlists the player belongs to
 * @param root0.onListPress - Manage-watchlists handler
 */
const WatchlistPills = ({ watchlists, onListPress }: WatchlistPillsProps) => {
  if (watchlists.length === 0) return null;

  const shown = watchlists.slice(0, MAX_PILLS);
  const overflow = watchlists.length - shown.length;

  return (
    <Stack direction="horizontal" className="flex-wrap items-center gap-1">
      {shown.map(list => (
        <RemovablePill
          key={list.id}
          label={list.name}
          onRemove={onListPress}
          onPress={onListPress}
          removeAccessibilityLabel={`Manage ${list.name} membership`}
        />
      ))}
      {overflow > 0 ? (
        <Text variant="micro" className="font-medium text-content-muted">
          +{overflow}
        </Text>
      ) : null}
    </Stack>
  );
};

/** Props for the card footer */
interface CardFooterProps {
  readonly updatedLabel: string | null | undefined;
  readonly scoutName: string | null | undefined;
}

/**
 * Right-aligned "Updated · scout" footer. Hides when both parts are absent.
 * @param root0 - Footer data
 * @param root0.updatedLabel - Pre-formatted updated label, or null
 * @param root0.scoutName - Scout / owner name, or null
 */
const CardFooter = ({ updatedLabel, scoutName }: CardFooterProps) => {
  const text = [updatedLabel, scoutName]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
  if (!text) return null;

  return (
    <Stack direction="horizontal" className="items-center justify-end">
      <Text variant="micro" className="text-content-secondary">
        {text}
      </Text>
    </Stack>
  );
};

/**
 * Renders a single scouting target card for the mobile list (v7 layout).
 * @param root0 - Card display data and handlers
 * @param root0.playerName - Player display name
 * @param root0.initials - Avatar initials
 * @param root0.clubName - Club name
 * @param root0.age - Player age in years (null hides the segment)
 * @param root0.monthsOnContract - Contract months (null hides the segment)
 * @param root0.gpr - GPR score or null
 * @param root0.scoreBadgeLabel - Short label prefix for the badge
 * @param root0.avgScoutRating - Overall avg scout rating (prefixes the chip)
 * @param root0.scoutReportCount - Report count for the reports chip
 * @param root0.squadStatus - Starting XI / squad status label (null hides chip)
 * @param root0.pipelineTier - Pipeline tier for the link (null hides it)
 * @param root0.updatedLabel - Footer "updated" label (null hides it)
 * @param root0.scoutName - Footer scout byline (null hides it)
 * @param root0.position - Primary position abbreviation
 * @param root0.positionExtraCount - Positions beyond the primary (`+N`)
 * @param root0.onPositionsPress - Callback when the position badge is tapped
 * @param root0.watchlists - All watchlists the player belongs to
 * @param root0.onListPress - Callback when a list pill is tapped
 * @param root0.onPress - Callback when the card is tapped
 */
const MobileTargetCardView = ({
  playerName,
  initials,
  clubName,
  age,
  monthsOnContract,
  gpr,
  scoreBadgeLabel,
  avgScoutRating,
  scoutReportCount,
  squadStatus,
  pipelineTier,
  updatedLabel,
  scoutName,
  position,
  positionExtraCount,
  onPositionsPress,
  watchlists,
  onListPress,
  onPress,
}: MobileTargetCardViewProps) => (
  <Stack className="px-4 pb-3">
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`View ${playerName}`}
      className="min-h-11"
      testID={TEST_ID_SCOUTING.MOBILE_TARGET_CARD}
    >
      <Stack className="gap-2 rounded-md border border-outline-subtle bg-surface-base p-3">
        {/* Row 1: avatar + name + score badge */}
        <Stack direction="horizontal" className="items-center gap-2">
          <InitialsAvatar initials={initials} />
          <Text
            variant="title-sm"
            numberOfLines={1}
            className="flex-1 text-content-primary"
          >
            {playerName}
          </Text>
          {gpr !== null ? (
            <RatingBadge value={gpr.toFixed(0)} label={scoreBadgeLabel} />
          ) : null}
        </Stack>

        {/* Row 2: position chip · club · age · contract */}
        <Stack direction="horizontal" className="items-center gap-2">
          <PositionBadge
            position={position}
            positionExtraCount={positionExtraCount}
            onPositionsPress={onPositionsPress}
            accessibilityLabel={`View all positions for ${playerName}`}
            testID={TEST_ID_SCOUTING.MOBILE_TARGET_POSITIONS}
          />
          <MetaRow
            segments={[
              clubName,
              age != null ? `${age}y` : null,
              monthsOnContract != null
                ? `Contract ${monthsOnContract}mo`
                : null,
            ]}
          />
        </Stack>

        {/* Row 3: status — Starting XI + reports chips, pipeline link */}
        <StatusRow
          squadStatus={squadStatus}
          avgScoutRating={avgScoutRating}
          scoutReportCount={scoutReportCount}
          pipelineTier={pipelineTier}
        />

        {/* Row 4: removable watchlist pills */}
        <WatchlistPills watchlists={watchlists} onListPress={onListPress} />

        {/* Row 5: updated · scout footer */}
        <CardFooter updatedLabel={updatedLabel} scoutName={scoutName} />
      </Stack>
    </Pressable>
  </Stack>
);

MobileTargetCardView.displayName = "MobileTargetCardView";

export default memo(MobileTargetCardView);
